// ECHTE MSAL — actief wanneer VITE_USE_MOCK_AUTH=false
// Minimalistische flow: één login pad via loginPopup, alleen OIDC scopes,
// localStorage cache (zie msalConfig.ts). Per tenant één instantie
// (zie authBackend.ts voor de cache).
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';

import { initialsFromName } from './initials';
import { buildMsalConfig, loginRequest, silentRequest } from './msalConfig';
import { SessionExpiredError } from './sessionExpiredError';
import type { TenantCode } from './tenants';
import type { AuthBackend, User } from './types';

function accountToUser(account: AccountInfo): User {
  const name = account.name ?? account.username ?? 'Onbekend';
  return {
    id: account.homeAccountId,
    name,
    initials: initialsFromName(name),
    activePerspective: null,
    perspectives: [],
  };
}

export function createMsalAuth(code: TenantCode): AuthBackend {
  const msalInstance = new PublicClientApplication(buildMsalConfig(code));
  let initialized = false;
  let currentUser: User | null = null;

  async function ensureInitialized(): Promise<void> {
    if (initialized) return;
    await msalInstance.initialize();
    initialized = true;
  }

  return {
    getUser() {
      return currentUser;
    },
    getCurrentEmail() {
      const username = msalInstance.getActiveAccount()?.username;
      return username && username.trim() !== '' ? username : null;
    },
    // Stuurt het ID-token mee als bearer, niet een access token voor een
    // custom API-scope: dat zou een "Expose an API"-registratie in Entra
    // vereisen die er (nog) niet is. Het ID-token is altijd voor deze eigen
    // SPA-client bedoeld (aud = clientId), wat al exact is wat de API als
    // audience verwacht, en bevat via de openid+profile-scopes uit
    // loginRequest de preferred_username-claim die resolveScope nodig heeft.
    async getAccessToken(forceRefresh = false) {
      await ensureInitialized();
      const account = msalInstance.getActiveAccount() ?? undefined;
      try {
        // forceRefresh hoort bij SilentRequest, niet bij PopupRequest — daarom
        // twee losse request-objecten.
        const result = await msalInstance.acquireTokenSilent({
          ...silentRequest,
          account,
          forceRefresh,
        });
        return result.idToken;
      } catch (error) {
        // Alleen bij interactie-vereist een popup; andere fouten doorgeven.
        if (!(error instanceof InteractionRequiredAuthError)) throw error;
        try {
          const result = await msalInstance.acquireTokenPopup({ ...loginRequest, account });
          return result.idToken;
        } catch (popupError) {
          // Opnieuw inloggen is nodig maar lukt niet: het venster wordt door de
          // browser geblokkeerd (deze aanroep komt niet uit een gebruikersactie),
          // de gebruiker sluit het, of het netwerk ligt eruit. In alle gevallen
          // is de sessie niet te herstellen — dat moet als zodanig herkenbaar
          // zijn, anders toont de UI "geen perspectief" in plaats van "log
          // opnieuw in".
          console.warn('[msalAuth] interactief opnieuw inloggen mislukt', popupError);
          throw new SessionExpiredError();
        }
      }
    },
    async loginWithSSO() {
      await ensureInitialized();
      const result = await msalInstance.loginPopup(loginRequest);
      msalInstance.setActiveAccount(result.account);
      currentUser = accountToUser(result.account);
      return currentUser;
    },
    async logout() {
      await ensureInitialized();
      const active = msalInstance.getActiveAccount();
      await msalInstance.logoutPopup({ account: active ?? undefined });
      currentUser = null;
    },
    async restoreSession() {
      await ensureInitialized();
      const accounts = msalInstance.getAllAccounts();
      const account = accounts[0];
      if (!account) {
        currentUser = null;
        return null;
      }
      msalInstance.setActiveAccount(account);
      // Een account in de cache bewijst niets: MSAL bewaart accounts en tokens
      // los van elkaar, dus het account blijft staan nadat de refresh token is
      // verlopen. Stil valideren voorkomt dat we "ingelogd" melden voor een dode
      // sessie — en dat de eerste API-call daarna via getAccessToken een popup
      // opent terwijl de gebruiker alleen maar een tab heeft geopend (zonder
      // klik blokkeert de browser die toch).
      try {
        await msalInstance.acquireTokenSilent({ ...silentRequest, account });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          currentUser = null;
          return null;
        }
        // Andere fouten (netwerk plat) zeggen niets over de geldigheid van de
        // sessie: niet uitloggen, maar /me laten beslissen.
        console.warn('[msalAuth] sessie niet stil te valideren', error);
      }
      currentUser = accountToUser(account);
      return currentUser;
    },
  };
}
